import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Navbar as BSNavbar, Container, Nav, Button, Spinner, Form } from 'react-bootstrap';
import { authAPI, avatarsAPI, plansAPI, paymentsAPI } from '../services/api';
import {
  extractAvatarsList,
  getPlanDisplayInfo,
  getPlanNameFromUser,
  isPaidPlanCardCurrent,
  mergeUserWithLatestSubscription,
  resolveUserPlanId,
  resolveUserPlanInterval,
  shouldUseLimitedInviteePlanDisplay,
  shouldShowGroupChatUpgradePlans,
  getLimitedInviteePlanDisplay,
  filterGroupChatUpgradePlans,
  canUserCreateAvatar,
} from '../utils/freeTrial';
import { getAvatarRelationLabel } from '../constants/relations';
import { toast } from 'react-toastify';
import CustomModal from './CustomModal';
import CustomButton from './CustomButton';
import './Navbar.css';

const getInitialUser = () => {
  try {
    const s = sessionStorage.getItem('user_data');
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
};

const formatPlanInterval = (interval) => {
  if (!interval || typeof interval !== 'string') return null;
  const i = interval.toLowerCase().trim();
  if (i === 'free_trial') return 'Free trial';
  if (i === 'monthly') return 'Monthly Plan';
  if (i === 'yearly' || i === 'year') return 'Yearly Plan';
  return `${interval.charAt(0).toUpperCase()}${interval.slice(1)} Plan`;
};

const groupAvatarPlansByType = (rawAvatarPlans) => {
  const plansByType = {};
  rawAvatarPlans.forEach((plan) => {
    const typeName = plan.avatar_type_name || 'Generic';
    if (!plansByType[typeName]) {
      plansByType[typeName] = {
        id: plan.avatar_type_id || Math.random(),
        name: typeName,
        plans: [],
      };
    }
    const normalizedPlan = { ...plan };
    const planInterval = (plan.interval || '').toLowerCase();
    const planPrice = Number(plan.price_usd || plan.price || 0);
    if (planInterval === 'yearly' || planInterval === 'year') {
      normalizedPlan.yearly_price_usd = planPrice;
      normalizedPlan.monthly_price_usd = null;
    } else if (planInterval === 'monthly' || planInterval === 'month') {
      normalizedPlan.monthly_price_usd = planPrice;
      normalizedPlan.yearly_price_usd = null;
    }
    plansByType[typeName].plans.push(normalizedPlan);
  });
  return Object.values(plansByType);
};

const normalizeAvatarPricingOption = (opt, type) => {
  const interval = (opt.interval || opt.interval_name || '').toLowerCase();
  if (interval === 'free_trial' || interval === 'free') return null;
  const price = Number(opt.price_usd || opt.price || 0);
  if (price <= 0) return null;
  const normalized = {
    ...opt,
    id: opt.plan_pricing_id || opt.id,
    avatar_type_id: type.avatar_type_id,
    avatar_type_name: type.avatar_type_name,
    price_usd: price,
    interval,
  };
  if (interval === 'yearly' || interval === 'year') {
    normalized.yearly_price_usd = price;
    normalized.monthly_price_usd = null;
  } else {
    normalized.monthly_price_usd = price;
    normalized.yearly_price_usd = null;
  }
  return normalized;
};

// getPlanNameFromUser imported from ../utils/freeTrial

const Navbar = ({ variant = 'default', disabled = false, navClassName = '' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [user, setUser] = useState(getInitialUser);
  const [avatars, setAvatars] = useState(() => {
    try {
      const s = sessionStorage.getItem('avatars_data');
      return s ? JSON.parse(s) : [];
    } catch {
      return [];
    }
  });
  const [plan, setPlan] = useState(() => {
    try {
      const u = getInitialUser();
      const uid = u?.id ?? u?.user_id ?? parseInt(sessionStorage.getItem('user_id') || '0', 10);
      return getPlanNameFromUser(u, uid || null);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  // Plan management state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [avatarPlans, setAvatarPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [avatarUpgradeInfo, setAvatarUpgradeInfo] = useState(null);
  const [autoRenew, setAutoRenew] = useState(null);

  const profileMenuRef = useRef(null);
  const userDataFetchedRef = useRef(false);
  const avatarsRefreshingRef = useRef(false);
  const token = localStorage.getItem('access_token');

  const isLoggedIn = !!token;

  // Sync autoRenew from user subscription
  useEffect(() => {
    if (user?.subscription?.auto_renew !== undefined) {
      setAutoRenew(!!user.subscription.auto_renew);
    }
  }, [user]);

  useEffect(() => {
    if (isLoggedIn && !userDataFetchedRef.current) {
      fetchUserData();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (user) {
      const uid = user.id ?? user.user_id ?? parseInt(sessionStorage.getItem('user_id') || '0', 10);
      const planName = getPlanNameFromUser(user, uid || null);
      setPlan(planName);
    }
  }, [user, location.pathname]);

  useEffect(() => {
    if (isLoggedIn && location.pathname === '/chat' && userDataFetchedRef.current && !avatarsRefreshingRef.current) {
      const avatarsFetchedInSession = sessionStorage.getItem('avatars_fetched');
      const storedAvatarsData = sessionStorage.getItem('avatars_data');

      if (!avatarsFetchedInSession || !storedAvatarsData) {
        const userId = user?.id || user?.user_id || parseInt(sessionStorage.getItem('user_id') || '0', 10);
        if (userId && userId > 0) {
          avatarsRefreshingRef.current = true;

          const timeoutId = setTimeout(() => {
            refreshAvatars(userId).finally(() => {
              avatarsRefreshingRef.current = false;
            });
          }, 1500);

          return () => {
            clearTimeout(timeoutId);
            avatarsRefreshingRef.current = false;
          };
        }
      }
    }
  }, [location.pathname, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && !sessionStorage.getItem('user_fetched')) {
      userDataFetchedRef.current = false;
      fetchUserData();
    }
  }, [location.pathname, isLoggedIn]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  const openBillingModal = () => {
    setShowProfileMenu(false);
    setShowBillingModal(true);
    if (!avatarPlans?.length || !addons?.length) {
      fetchUpgradePlans();
    }
  };

  useEffect(() => {
    if (!showBillingModal) return;
    let cancelled = false;
    setBillingLoading(true);
    if (!avatarPlans?.length) fetchUpgradePlans();
    (async () => {
      try {
        const userResponse = await authAPI.getCurrentUser();
        if (cancelled || !userResponse?.status || !userResponse?.data) return;
        const userData = await mergeUserWithLatestSubscription(userResponse.data);
        setUser(userData);
        sessionStorage.setItem('user_data', JSON.stringify(userData));
      } catch (_) { /* non-critical */ }
      finally {
        if (!cancelled) setBillingLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showBillingModal]);

  const fetchUserData = async () => {
    const userFetchedInSession = sessionStorage.getItem('user_fetched');
    const avatarsFetchedInSession = sessionStorage.getItem('avatars_fetched');

    const storedUserData = sessionStorage.getItem('user_data');
    if (storedUserData) {
      try {
        const parsedUser = JSON.parse(storedUserData);
        setUser(parsedUser);

        const uid = parsedUser?.id ?? parsedUser?.user_id ?? parseInt(sessionStorage.getItem('user_id') || '0', 10);
        const planName = getPlanNameFromUser(parsedUser, uid || null);
        setPlan(planName);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
      }
    }

    const storedAvatarsData = sessionStorage.getItem('avatars_data');
    if (storedAvatarsData) {
      try {
        const parsedAvatars = JSON.parse(storedAvatarsData);
        setAvatars(parsedAvatars);
      } catch (error) {
        console.error('Error parsing stored avatars data:', error);
      }
    }

    if (userFetchedInSession && avatarsFetchedInSession) {
      return;
    }

    if (userDataFetchedRef.current) {
      return;
    }

    try {
      userDataFetchedRef.current = true;
      setLoading(true);

      let userId = null;
      if (!userFetchedInSession) {
        sessionStorage.setItem('user_fetched', 'true');
        const userResponse = await authAPI.getCurrentUser();
        if (userResponse.status && userResponse.data) {
          const userData = userResponse.data;
          setUser(userData);
          userId = userData.id || userData.user_id;

          try {
            sessionStorage.setItem('user_data', JSON.stringify(userData));
          } catch (error) {
            console.warn('Could not store user data in sessionStorage:', error);
          }

          if (userId) {
            sessionStorage.setItem('user_id', userId.toString());
          }

          const planName = getPlanNameFromUser(userData, userId || null);
          setPlan(planName);
        }
      } else {
        const storedUserId = sessionStorage.getItem('user_id');
        if (storedUserId) {
          userId = parseInt(storedUserId, 10);
        }

        if (!user && storedUserData) {
          try {
            const parsedUser = JSON.parse(storedUserData);
            setUser(parsedUser);
            const uid = parsedUser?.id ?? parsedUser?.user_id ?? userId;
            const planName = getPlanNameFromUser(parsedUser, uid || null);
            setPlan(planName);
          } catch (error) {
            console.error('Error parsing stored user data:', error);
          }
        }
      }

      if (userId && !avatarsFetchedInSession) {
        sessionStorage.setItem('avatars_fetched', 'true');
        try {
          const avatarsResponse = await avatarsAPI.getMyDetails();
          const avatarsList = extractAvatarsList(avatarsResponse);
          if (avatarsList.length > 0 || avatarsResponse?.status !== false) {
            setAvatars(avatarsList);
            const hasAvatarsValue = avatarsList.length > 0;
            sessionStorage.setItem('has_avatars', hasAvatarsValue.toString());

            try {
              sessionStorage.setItem('avatars_data', JSON.stringify(avatarsList));
            } catch (error) {
              console.warn('Could not store avatars data in sessionStorage:', error);
            }
          }
        } catch (error) {
          console.error('Error fetching avatars:', error);
          sessionStorage.removeItem('avatars_fetched');
        }
      }

    } catch (error) {
      console.error('Error fetching user data:', error);
      userDataFetchedRef.current = false;
      sessionStorage.removeItem('user_fetched');
      sessionStorage.removeItem('avatars_fetched');
      sessionStorage.removeItem('has_avatars');
      sessionStorage.removeItem('user_id');
      sessionStorage.removeItem('user_data');
      sessionStorage.removeItem('avatars_data');
    } finally {
      setLoading(false);
    }
  };

  const refreshAvatars = async (userId) => {
    if (!userId) {
      const storedUserId = sessionStorage.getItem('user_id');
      if (storedUserId) {
        userId = parseInt(storedUserId, 10);
      } else {
        return;
      }
    }

    try {
      setLoading(true);

      const avatarsResponse = await avatarsAPI.getMyDetails();
      const fetchedAvatars = extractAvatarsList(avatarsResponse);
      setAvatars(fetchedAvatars);
      const hasAvatarsValue = fetchedAvatars.length > 0;

      sessionStorage.setItem('avatars_fetched', 'true');
      sessionStorage.setItem('has_avatars', hasAvatarsValue.toString());
      try {
        sessionStorage.setItem('avatars_data', JSON.stringify(fetchedAvatars));
      } catch (error) {
        console.warn('Could not store avatars data in sessionStorage:', error);
      }
    } catch (error) {
      console.error('Error refreshing avatars:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Plan management ──────────────────────────────────────────────────────────

  const getPlanDetails = () => {
    const display = getPlanDisplayInfo(user);
    const subscription = user?.subscription;
    const currentAutoRenew = autoRenew !== null ? autoRenew : !!(subscription?.auto_renew);
    const planIntervalLabel = display.planIntervalLabel
      || (display.planInterval ? formatPlanInterval(display.planInterval) : null);

    return {
      planName: display.planName || plan || null,
      planInterval: display.planInterval,
      planIntervalLabel,
      planPrice: display.planPriceLabel || display.planPrice,
      currentAutoRenew,
      hasSubscription: display.hasSubscription || !!(subscription || display.planName),
      isFreeTrial: display.isFreeTrial,
    };
  };

  const fetchUpgradePlans = async () => {
    setLoadingAddons(true);
    try {
      const [upgradeRes, addonsRes] = await Promise.all([
        avatarsAPI.getUpgradePlans(),
        plansAPI.getAddons()
      ]);

      let extractedAddons = [];
      let extractedAvatarPlans = [];

      const responseData = (upgradeRes && upgradeRes.data) || upgradeRes;
      const addonsData = (addonsRes && addonsRes.data) || addonsRes;

      if (Array.isArray(addonsData)) {
        extractedAddons = addonsData;
      } else if (addonsData && Array.isArray(addonsData.items)) {
        extractedAddons = addonsData.items;
      } else if (addonsData && Array.isArray(addonsData.addons)) {
        extractedAddons = addonsData.addons;
      }

      // Profile Subscriptions: full catalog from addons API (same as AvatarChatPage billing modal).
      // upgrade-plan excludes types the user already owns, so it must not be the only source.
      if (addonsData && Array.isArray(addonsData.avatar_plans) && addonsData.avatar_plans.length > 0) {
        extractedAvatarPlans = groupAvatarPlansByType(addonsData.avatar_plans);
      }

      if (responseData) {
        if (responseData.weekly_tokens_remaining !== undefined) {
          setAvatarUpgradeInfo({
            weekly_tokens_remaining: responseData.weekly_tokens_remaining,
            monthly_tokens_remaining: responseData.monthly_tokens_remaining,
            base_tokens: responseData.base_tokens
          });
        }

        if (responseData.addons && Array.isArray(responseData.addons)) {
          extractedAddons = responseData.addons;
        }

        if (!extractedAvatarPlans.length) {
          if (responseData.avatar_types && Array.isArray(responseData.avatar_types)) {
            extractedAvatarPlans = responseData.avatar_types.map(type => {
              const plans = (type.pricing_options || [])
                .map(opt => normalizeAvatarPricingOption(opt, type))
                .filter(Boolean);
              return { id: type.avatar_type_id, name: type.avatar_type_name, plans };
            }).filter(type => (type.plans || []).length > 0);
          } else if (responseData.avatar_plans && Array.isArray(responseData.avatar_plans)) {
            extractedAvatarPlans = groupAvatarPlansByType(responseData.avatar_plans);
          }
        }
      }

      setAddons(extractedAddons);
      setAvatarPlans(extractedAvatarPlans);
    } catch (error) {
      console.error('Error fetching upgrade plans:', error);
      toast.error('Failed to load upgrade plans.');
    } finally {
      setLoadingAddons(false);
    }
  };

  const handleToggleAutoRenew = async () => {
    const primaryAvatarId = avatars[0]?.id;
    if (!primaryAvatarId) return;
    const next = !autoRenew;
    setAutoRenew(next);
    try {
      await avatarsAPI.updateAvatarProfile(primaryAvatarId, { auto_renew: next });
    } catch (error) {
      console.error('Error toggling auto-renew:', error);
      toast.error(error.message || 'Failed to update auto-renew setting');
      setAutoRenew(!next);
    }
  };

  const handleCancelSubscription = async () => {
    setShowCancelConfirmModal(false);
    setCancellingSubscription(true);
    try {
      await avatarsAPI.cancelSubscription();
      // Clear cached user data so plan info refreshes
      sessionStorage.removeItem('user_fetched');
      sessionStorage.removeItem('user_data');
      userDataFetchedRef.current = false;
      fetchUserData();
    } catch (error) {
      console.error('Cancel subscription failed:', error);
      toast.error(error.message || 'Failed to cancel subscription');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleSubscriptionPayment = async (plan, interval) => {
    if (!plan || !plan.id) {
      toast.error('Invalid plan selected');
      return;
    }

    // Open blank popup NOW — synchronous, inside the user gesture — before any await.
    // Browsers only honour window.open() without blocking when called directly from a click.
    const popupWidth = 520;
    const popupHeight = 700;
    const left = Math.round(window.screenX + (window.outerWidth - popupWidth) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - popupHeight) / 2);
    const upgradePopup = window.open(
      'about:blank',
      'PayPalUpgrade',
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );
    const popupBlocked = !upgradePopup || upgradePopup.closed || typeof upgradePopup.closed === 'undefined';

    try {
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/payment/success`;
      const currentUrl = window.location.href.split('?')[0];

      const response = await paymentsAPI.createPaypalOrder({
        avatar_plan_id: plan.id,
        plan_interval: interval,
        return_url: successUrl,
        cancel_url: currentUrl
      });

      let approveUrl = null;
      if (response?.approve_url) {
        approveUrl = response.approve_url;
      } else if (response?.data?.approve_url) {
        approveUrl = response.data.approve_url;
      } else if (response?.links && Array.isArray(response.links)) {
        const link = response.links.find(l => l.rel === 'approve');
        if (link) approveUrl = link.href;
      } else if (response?.data?.links && Array.isArray(response.data.links)) {
        const link = response.data.links.find(l => l.rel === 'approve');
        if (link) approveUrl = link.href;
      }

      if (approveUrl) {
        let orderId = response?.order_id || response?.data?.order_id;
        if (!orderId) {
          try { orderId = new URL(approveUrl).searchParams.get('token'); } catch (e) { }
        }
        localStorage.setItem('paypal_checkout_context', JSON.stringify({
          order_id: orderId,
          avatar_plan_id: plan.id,
          plan_interval: interval,
          auto_renew: true,
          started_from: '/chat',
          context: 'popup'
        }));

        if (popupBlocked) {
          window.location.href = approveUrl;
          return;
        }

        // Navigate the already-open blank popup to the real PayPal URL
        upgradePopup.location.href = approveUrl;

        const upgradeMessageHandler = (event) => {
          if (event.origin !== window.location.origin) return;

          if (event.data?.type === 'PAYPAL_SUCCESS') {
            console.log('Navbar: Received PayPal success message from popup');
            window.removeEventListener('message', upgradeMessageHandler);
            clearInterval(upgradePollTimer);
            fetchUserData();
            toast.success('Subscription updated successfully!');
          } else if (event.data?.type === 'PAYPAL_ERROR') {
            console.warn('Navbar: Received PayPal error message from popup');
            window.removeEventListener('message', upgradeMessageHandler);
            clearInterval(upgradePollTimer);
            toast.error(event.data.error || 'Payment failed.');
          }
        };
        const upgradePollTimer = setInterval(() => {
          if (upgradePopup.closed) {
            window.removeEventListener('message', upgradeMessageHandler);
            clearInterval(upgradePollTimer);
          }
        }, 800);
        window.addEventListener('message', upgradeMessageHandler);

      } else {
        if (!popupBlocked) upgradePopup.close();
        const msg = (typeof response?.msg === 'string' ? response.msg : null) ||
          (typeof response?.message === 'string' ? response.message : null) ||
          'No approval URL found';
        toast.error(`Failed to initiate payment: ${msg}`);
      }
    } catch (error) {
      if (upgradePopup && !upgradePopup.closed) upgradePopup.close();
      console.error('Payment error:', error);
      toast.error(error.message || error.msg || 'Payment initialization failed');
    }
  };

  const handleAddonPayment = async (addon) => {
    if (!addon || !addon.id) {
      toast.error('Invalid upgrade plan selected');
      return;
    }

    // Open blank popup NOW
    const popupWidth = 520;
    const popupHeight = 700;
    const left = Math.round(window.screenX + (window.outerWidth - popupWidth) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - popupHeight) / 2);
    const upgradePopup = window.open(
      'about:blank',
      'PayPalAddon',
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );
    const popupBlocked = !upgradePopup || upgradePopup.closed || typeof upgradePopup.closed === 'undefined';

    try {
      localStorage.setItem('pending_addon_plan_id', addon.id);

      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/payment/addon/success`;
      const currentUrl = window.location.href.split('?')[0];

      const response = await plansAPI.createAddonPayPalOrder({
        addon_plan_id: addon.id,
        return_url: successUrl,
        cancel_url: currentUrl
      });

      let approveUrl = null;
      if (response?.approve_url) {
        approveUrl = response.approve_url;
      } else if (response?.data?.approve_url) {
        approveUrl = response.data.approve_url;
      } else if (response?.links && Array.isArray(response.links)) {
        const link = response.links.find(l => l.rel === 'approve');
        if (link) approveUrl = link.href;
      } else if (response?.data?.links && Array.isArray(response.data.links)) {
        const link = response.data.links.find(l => l.rel === 'approve');
        if (link) approveUrl = link.href;
      }

      if (approveUrl) {
        if (popupBlocked) {
          window.location.href = approveUrl;
          return;
        }

        // Navigate the already-open blank popup to the real PayPal URL
        upgradePopup.location.href = approveUrl;

        const addonMessageHandler = (event) => {
          if (event.origin !== window.location.origin) return;

          if (event.data?.type === 'PAYPAL_SUCCESS' && event.data?.context === 'addon') {
            console.log('Navbar: Received PayPal success message from popup (addon)');
            window.removeEventListener('message', addonMessageHandler);
            clearInterval(addonPollTimer);
            fetchUserData();
            toast.success('Add-on purchased successfully!');
          }
        };
        const addonPollTimer = setInterval(() => {
          if (upgradePopup.closed) {
            window.removeEventListener('message', addonMessageHandler);
            clearInterval(addonPollTimer);
          }
        }, 800);
        window.addEventListener('message', addonMessageHandler);

      } else {
        if (!popupBlocked) upgradePopup.close();
        const msg = (typeof response?.msg === 'string' ? response.msg : null) ||
          (typeof response?.message === 'string' ? response.message : null) ||
          'No approval URL found';
        toast.error(`Failed to initiate payment: ${msg}`);
      }
    } catch (error) {
      if (upgradePopup && !upgradePopup.closed) upgradePopup.close();
      console.error('Payment error:', error);
      toast.error(error.message || error.msg || 'Payment initialization failed');
    }
  };

  // ── Auth ─────────────────────────────────────────────────────────────────────

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('chat_session_id');
    localStorage.removeItem('chat_session_project_id');
    localStorage.removeItem('chat_messages');
    sessionStorage.removeItem('user_fetched');
    sessionStorage.removeItem('avatars_fetched');
    sessionStorage.removeItem('has_avatars');
    sessionStorage.removeItem('user_id');
    sessionStorage.removeItem('user_data');
    sessionStorage.removeItem('avatars_data');
    sessionStorage.removeItem('plans_fetched');
    sessionStorage.removeItem('first_month_discount_active');
    sessionStorage.removeItem('discount_banner_dismissed');
    localStorage.removeItem('signup_invite_params');
    setUser(null);
    setAvatars([]);
    setPlan(null);
    setAutoRenew(null);
    userDataFetchedRef.current = false;
    navigate('/');
    setShowProfileMenu(false);
  };

  // ── Display helpers ───────────────────────────────────────────────────────────

  const getUserInitials = () => {
    if (user) {
      const firstName = user.first_name || user.firstName || user.given_name || '';
      const lastName = user.last_name || user.lastName || user.family_name || '';
      if (firstName && lastName) {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
      }
      if (firstName) return firstName.charAt(0).toUpperCase();
      if (lastName) return lastName.charAt(0).toUpperCase();
      if (user.email) return user.email.charAt(0).toUpperCase();
      if (user.full_name) {
        const parts = user.full_name.trim().split(/\s+/);
        if (parts.length >= 2) return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
        if (parts[0]) return parts[0].charAt(0).toUpperCase();
      }
      if (user.username) return user.username.charAt(0).toUpperCase();
      if (user.name) {
        const parts = user.name.trim().split(/\s+/);
        if (parts.length >= 2) return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
        if (parts[0]) return parts[0].charAt(0).toUpperCase();
      }
    }
    return 'U';
  };

  const getDisplayName = () => {
    if (!user) return null;
    const first = user.first_name || user.firstName || user.given_name || '';
    const last = user.last_name || user.lastName || user.family_name || '';
    const fullName = `${first} ${last}`.trim();
    return fullName || user.full_name || user.username || user.name || user.email || null;
  };

  const handleCreateAvatarClick = async (e) => {
    if (e) e.preventDefault();
    if (!isLoggedIn) {
      navigate('/signin');
      return;
    }
    if (avatars.length > 0 || !canUserCreateAvatar(user)) {
      navigate('/chat');
      return;
    }
    navigate('/create-avatar');
  };

  // ── Upgrade modal rendering ───────────────────────────────────────────────────

  const renderUpgradeModalContent = () => {
    const { planInterval, isFreeTrial } = getPlanDetails();
    const currentPlanInterval = (planInterval || 'monthly').toLowerCase();
    const isMonthly = currentPlanInterval === 'monthly';
    const isYearly = currentPlanInterval === 'yearly' || currentPlanInterval === 'year';

    let filteredAvatarPlans = avatarPlans.map(type => {
      const plans = (type.plans || []).filter(p => (p.interval || '').toLowerCase() !== 'free_trial');
      return { ...type, plans };
    }).filter(type => type.plans.length > 0);

    let filteredAddons = addons;
    if (isMonthly) {
      filteredAddons = addons.filter(a => {
        const name = (a.name || '').toLowerCase();
        return name.includes('tone') || name.includes('token');
      });
    }

    return (
      <div style={{ padding: '0 0 20px 0', maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
        {filteredAvatarPlans.length > 0 && (
          <div style={{ marginBottom: '30px', padding: '0 15px' }}>
            <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: 'var(--soft-black)', textAlign: 'center' }}>Avatar Subscriptions</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
              {filteredAvatarPlans.map(type =>
                (type.plans || []).map(p => {
                  const interval = (p.interval || '').toLowerCase();
                  const isYearlyPlan = interval === 'yearly' || interval === 'year' || Number(p.yearly_price_usd) > 0;
                  let priceText = '';
                  if (p.price_usd !== undefined) {
                    priceText = `$${p.price_usd}/${interval === 'yearly' || interval === 'year' ? 'year' : 'month'}`;
                  } else {
                    priceText = isYearlyPlan ? `$${p.yearly_price_usd}/year` : `$${p.monthly_price_usd}/month`;
                  }
                  const billingPeriod = isYearlyPlan ? 'yearly' : 'monthly';
                  if (billingPeriod === 'monthly' && isYearly) return null;
                  const isCurrentPlan = isPaidPlanCardCurrent({
                    isFreeTrial,
                    userPlanId: user?.subscription?.plan_id,
                    planId: p.id,
                    userInterval: currentPlanInterval,
                    billingPeriod,
                  });
                  return (
                    <div key={`avatar-plan-${p.id}-${p.interval}`} style={{ background: isCurrentPlan ? 'rgba(107,158,160,0.04)' : 'white', border: isCurrentPlan ? '2px solid var(--soft-teal)' : '1px solid var(--border-subtle)', borderRadius: '16px', padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                      {isCurrentPlan && (
                        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--soft-teal)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Your current plan</div>
                      )}
                      <div style={{ marginBottom: '16px' }}>
                        <h5 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: 'var(--soft-black)' }}>{isYearlyPlan ? 'Annual' : 'Monthly'}</h5>
                        <div style={{ marginTop: '5px', fontSize: '14px', fontWeight: 'bold', color: 'var(--soft-teal)' }}>{priceText}</div>
                      </div>
                      <button disabled={isCurrentPlan} onClick={() => { if (!isCurrentPlan) { setShowSubscriptionModal(false); handleSubscriptionPayment(p, billingPeriod); } }} style={{ background: isCurrentPlan ? '#e2e8f0' : 'var(--soft-teal)', color: isCurrentPlan ? 'var(--slate)' : 'white', border: 'none', padding: '10px 32px', borderRadius: '24px', fontSize: '15px', fontWeight: '600', cursor: isCurrentPlan ? 'default' : 'pointer' }}>{isCurrentPlan ? 'Current Plan' : 'Buy'}</button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {filteredAvatarPlans.length > 0 && (loadingAddons || addons.length > 0) && (
          <hr style={{ borderColor: 'var(--border-subtle)', margin: '20px 0' }} />
        )}

        <div style={{ padding: '0 15px' }}>
          <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: 'var(--soft-black)', textAlign: 'center' }}>Add-on Plans</h4>
          {loadingAddons ? (
            <div style={{ textAlign: 'center', padding: '20px' }}><Spinner animation="border" size="sm" /></div>
          ) : filteredAddons.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
              {filteredAddons.map(addon => (
                <div key={addon.id} style={{ background: 'white', border: '1px solid var(--border-subtle)', borderRadius: '16px', padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <h5 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: 'var(--soft-black)' }}>{addon.name}</h5>
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--slate)' }}>{addon.description}</p>
                    <div style={{ marginTop: '5px', fontSize: '14px', fontWeight: 'bold', color: 'var(--soft-teal)' }}>${addon.price_usd}</div>
                  </div>
                  <button onClick={() => handleAddonPayment(addon)} style={{ background: 'var(--soft-teal)', color: 'white', border: 'none', padding: '10px 32px', borderRadius: '24px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>Buy</button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '10px', color: 'var(--slate)' }}>No add-ons available at the moment.</div>
          )}
        </div>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  const { planName, planInterval, planPrice, currentAutoRenew, hasSubscription } = getPlanDetails();

  return (
    <>
      <BSNavbar
        className={`custom-navbar ${variant === 'transparent' ? 'navbar-transparent' : ''} ${navClassName}`}
        expand="lg"
        fixed="top"
      >
        <Container fluid className="navbar-container">
          <Link to="/" className="logo">
            <img src={process.env.PUBLIC_URL + '/Sayagain.png'} alt="SayAgain" className="logo-img" />
          </Link>
          <Nav className="nav-links">
            {isLoggedIn ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div className="profile-menu-wrapper" ref={profileMenuRef}>
                  <button
                    className="profile-menu-trigger"
                    onClick={() => !disabled && setShowProfileMenu(!showProfileMenu)}
                    style={disabled ? { cursor: 'default', opacity: 0.8 } : {}}
                    disabled={disabled}
                  >
                    <div className="profile-avatar-circle">
                      {getUserInitials()}
                    </div>
                  </button>

                  {showProfileMenu && (
                    <div className="profile-menu-dropdown">
                      {/* 1. User name + email */}
                      <div
                        className="profile-menu-header"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowProfileMenu(false);
                          navigate('/chat');
                        }}
                      >
                        <div className="profile-menu-avatar">
                          {getUserInitials()}
                        </div>
                        <div className="profile-menu-info">
                          <div className="profile-menu-name">
                            {getDisplayName() || user?.email || 'User'}
                          </div>
                          <div className="profile-menu-email">{user?.email || ''}</div>
                        </div>
                      </div>

                      {/* 2. Plan & Billing link */}
                      <div className="profile-menu-section">
                        <button
                          className="profile-menu-action"
                          onClick={openBillingModal}
                          style={{ width: '100%' }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                            <line x1="1" y1="10" x2="23" y2="10"></line>
                          </svg>
                          <span>Plan &amp; Billing</span>
                        </button>
                      </div>

                      {/* 3. Avatars list */}
                      <div className="profile-menu-section">
                        <div className="profile-menu-section-title">Avatar</div>
                        <div className="profile-menu-avatars">
                          {loading ? (
                            <div className="profile-menu-loading">Loading...</div>
                          ) : avatars.length > 0 ? (
                            avatars.map((avatar) => {
                              const relationLabel = getAvatarRelationLabel(
                                avatar,
                                user?.id || user?.user_id || sessionStorage.getItem('user_id')
                              );
                              return (
                              <div
                                key={avatar.id}
                                className="profile-menu-avatar-item"
                                onClick={() => { setShowProfileMenu(false); navigate('/chat'); }}
                                style={{ cursor: 'pointer' }}
                              >
                                <div className="profile-menu-avatar-initials">
                                  {`${avatar.first_name?.charAt(0) || ''}${avatar.last_name?.charAt(0) || ''}`.toUpperCase()}
                                </div>
                                <div className="profile-menu-avatar-details">
                                  <div className="profile-menu-avatar-name">
                                    {`${avatar.first_name || ''} ${avatar.last_name || ''}`.trim()}
                                  </div>
                                  {/* {relationLabel && (
                                    <div className="profile-menu-avatar-relation">{relationLabel}</div>
                                  )} */}
                                </div>
                                {/* <div className="profile-menu-avatar-status">
                                  {avatar.status === 'active' ? '● Active' : ''}
                                </div> */}
                              </div>
                            );
                            })
                          ) : (
                            <div className="profile-menu-no-avatars">No avatars yet</div>
                          )}
                        </div>
                      </div>

                      {/* 4. Logout */}
                      <div className="profile-menu-actions">
                        <button
                          onClick={handleLogout}
                          className="profile-menu-action profile-menu-logout"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                            <polyline points="16 17 21 12 16 7"></polyline>
                            <line x1="21" y1="12" x2="9" y2="12"></line>
                          </svg>
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="nav-auth-links">
                <Link to="/signin" className="nav-link-custom nav-signin-link">
                  Sign In
                </Link>
                <Link to="/signup" className="nav-cta-link">
                  <Button className="nav-cta">Get Started</Button>
                </Link>
              </div>
            )}
          </Nav>
        </Container>
      </BSNavbar>

      {/* Cancel Confirmation Modal */}
      <CustomModal
        show={showCancelConfirmModal}
        onHide={() => setShowCancelConfirmModal(false)}
        title="Cancel Subscription"
        subtitle="Are you sure you want to cancel your subscription?"
        iconVariant="danger"
        size="md"
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        }
      >
        <div style={{ textAlign: 'center', padding: '10px 0' }}>
          <p style={{ color: 'var(--slate)', marginBottom: '25px' }}>
            This will stop future billing but you will keep access until the end of the current period.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
            <CustomButton variant="secondary" onClick={() => setShowCancelConfirmModal(false)} style={{ minWidth: '120px' }}>Keep Plan</CustomButton>
            <CustomButton variant="danger" onClick={handleCancelSubscription} loading={cancellingSubscription} style={{ minWidth: '120px' }}>Proceed</CustomButton>
          </div>
        </div>
      </CustomModal>

      {/* Plan & Billing Modal */}
      {showBillingModal && (() => {
        const showLimitedInviteeBilling = shouldUseLimitedInviteePlanDisplay(user);
        const showGroupChatUpgradePlans = shouldShowGroupChatUpgradePlans(user);
        const planDetails = showLimitedInviteeBilling
          ? { ...getLimitedInviteePlanDisplay(), currentAutoRenew: autoRenew !== null ? autoRenew : !!(user?.subscription?.auto_renew) }
          : getPlanDetails();
        const { planName, planInterval, planIntervalLabel, planPrice, currentAutoRenew, hasSubscription, isFreeTrial } = planDetails;
        const upgradePlanTypes = showGroupChatUpgradePlans
          ? filterGroupChatUpgradePlans(avatarPlans)
          : (showLimitedInviteeBilling ? [] : avatarPlans);
        const userPlanId = resolveUserPlanId(user);
        const userInterval = resolveUserPlanInterval(user, avatarPlans) || (planInterval || '').toLowerCase();
        const renewalDate = user?.subscription?.end_date
          ? new Date(user.subscription.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
          : null;
        const canShowBilling = !billingLoading;
        return (
          <CustomModal
            show={showBillingModal}
            onHide={() => setShowBillingModal(false)}
            title="Plan & Billing"
            size="lg"
          >
            <div style={{ padding: '4px 0 20px 0', maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
              {billingLoading && (
                <div style={{ padding: '12px 15px', marginBottom: '12px', color: 'var(--slate)', fontSize: '14px' }}>
                  Loading your plan…
                </div>
              )}

              {canShowBilling && (
              <>
              <div style={{ marginBottom: '30px', padding: '0 15px' }}>
                <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: 'var(--soft-black)', textAlign: 'center' }}>
                  {showLimitedInviteeBilling ? 'Group Chat Plans' : (isFreeTrial ? 'Upgrade Plan' : 'Profile Subscriptions')}
                </h4>
                <div className="addons-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                  {upgradePlanTypes && upgradePlanTypes.length > 0 ? (
                    upgradePlanTypes.map((type) => {
                      const plansList = type.plans || [];
                      if (plansList.length === 0) return null;
                      return plansList.map((plan) => {
                        const isYearly = Number(plan.yearly_price_usd) > 0;
                        const isMonthly = Number(plan.monthly_price_usd) > 0;
                        if (!isYearly && !isMonthly) return null;
                        const billingPeriod = isYearly ? 'yearly' : 'monthly';
                        if (billingPeriod === 'monthly' && userInterval === 'yearly') return null;
                        const priceText = isYearly ? `$${plan.yearly_price_usd}/year` : `$${plan.monthly_price_usd}/month`;
                        const isCurrentPlan = showLimitedInviteeBilling
                          ? false
                          : isPaidPlanCardCurrent({
                            isFreeTrial,
                            userPlanId,
                            planId: plan.id,
                            userInterval,
                            billingPeriod,
                          });
                        return (
                          <div key={`avatar-plan-${plan.id}-${billingPeriod}`} style={{ display: 'flex' }}>
                            <div style={{
                              background: isCurrentPlan ? 'rgba(107,158,160,0.04)' : 'white',
                              border: isCurrentPlan ? '2px solid var(--soft-teal)' : '1px solid var(--border-subtle)',
                              borderRadius: '16px',
                              padding: '24px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              textAlign: 'center',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                              flex: 1,
                            }}>
                              {isCurrentPlan && (
                                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--soft-teal)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                                  Your current plan
                                </div>
                              )}
                              <div style={{ marginBottom: '16px' }}>
                                <h5 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: 'var(--soft-black)' }}>
                                  {isYearly ? 'Annual' : 'Monthly'}
                                </h5>
                                <div style={{ marginTop: '5px', fontSize: '14px', fontWeight: 'bold', color: 'var(--soft-teal)' }}>
                                  {priceText}
                                </div>
                                {isYearly && (
                                  <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--soft-teal)' }}>
                                    Save 37% vs monthly
                                  </div>
                                )}
                              </div>
                              <button
                                disabled={isCurrentPlan}
                                onClick={() => {
                                  if (!isCurrentPlan) {
                                    setShowBillingModal(false);
                                    handleSubscriptionPayment(plan, billingPeriod);
                                  }
                                }}
                                style={{
                                  background: isCurrentPlan ? '#e2e8f0' : 'var(--soft-teal)',
                                  color: isCurrentPlan ? 'var(--slate)' : 'white',
                                  border: 'none',
                                  padding: '10px 32px',
                                  borderRadius: '24px',
                                  fontSize: '15px',
                                  fontWeight: '600',
                                  cursor: isCurrentPlan ? 'default' : 'pointer',
                                  whiteSpace: 'nowrap',
                                  marginTop: '8px',
                                }}
                              >
                                {isCurrentPlan ? 'Current Plan' : 'Select'}
                              </button>
                            </div>
                          </div>
                        );
                      });
                    })
                  ) : showLimitedInviteeBilling && !showGroupChatUpgradePlans ? (
                    <div style={{ textAlign: 'center', padding: '10px', color: 'var(--slate)' }}>
                      Profile subscriptions are managed by your family group owner.
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '10px', color: 'var(--slate)' }}>
                      {loadingAddons ? 'Loading avatar subscriptions...' : 'No subscription plans available.'}
                    </div>
                  )}
                </div>
              </div>

              {!showLimitedInviteeBilling && (
              <>
              <hr style={{ borderColor: 'var(--border-subtle)', margin: '20px 0' }} />

              <div style={{ padding: '0 15px' }}>
                <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: 'var(--soft-black)', textAlign: 'center' }}>Add-on Plans</h4>
                {loadingAddons ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spinner animation="border" size="sm" />
                    <p style={{ marginTop: '10px', color: 'var(--slate)' }}>Loading available addons...</p>
                  </div>
                ) : addons.length > 0 ? (
                  <div className="addons-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                    {addons.map((addon) => (
                      <div key={addon.id} style={{
                        background: 'white',
                        border: '1px solid var(--border-subtle)',
                        borderRadius: '16px',
                        padding: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      }}>
                        <div style={{ marginBottom: '16px' }}>
                          <h5 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: 'var(--soft-black)' }}>
                            {addon.name}
                          </h5>
                          <p style={{ margin: 0, fontSize: '13px', color: 'var(--slate)' }}>
                            {addon.description === 'Continuing conversation beyond plan limit.'
                              ? 'Continue your conversations when your monthly allocation runs out.'
                              : addon.description || 'Enhance your experience'}
                          </p>
                          <div style={{ marginTop: '5px', fontSize: '14px', fontWeight: 'bold', color: 'var(--soft-teal)' }}>
                            ${addon.price_usd}
                            {addon.tokens && (
                              <span style={{ fontSize: '12px', fontWeight: 'normal', color: 'var(--slate)', marginLeft: '8px' }}>
                                • Equal to 1/4 of monthly prompts.
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddonPayment(addon)}
                          style={{
                            background: 'var(--soft-teal)',
                            color: 'white',
                            border: 'none',
                            padding: '10px 32px',
                            borderRadius: '24px',
                            fontSize: '15px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            marginTop: '8px',
                          }}
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: 'var(--slate)' }}>
                    No addons available at the moment.
                  </div>
                )}
              </div>

              <hr style={{ borderColor: 'var(--border-subtle)', margin: '20px 0' }} />
              </>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '0 15px' }}>
                {!showLimitedInviteeBilling && (
                <CustomButton
                  variant="secondary"
                  style={{ width: '100%' }}
                  onClick={() => window.open('https://www.paypal.com/myaccount/autopay', '_blank', 'noopener,noreferrer')}
                >
                  Update Payment Method
                </CustomButton>
                )}

                <div style={{ marginTop: '10px', marginBottom: '4px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Current Plan</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {(planName || !hasSubscription) && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--slate)' }}>Plan</span>
                        <span style={{ fontWeight: 500 }}>{planName || 'Free Trial'}</span>
                      </div>
                    )}
                    {planInterval && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--slate)' }}>{isFreeTrial ? 'Trial period' : 'Billing'}</span>
                        <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{planIntervalLabel || planInterval}</span>
                      </div>
                    )}
                    {planPrice && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--slate)' }}>Price</span>
                        <span style={{ fontWeight: 500 }}>{planPrice}</span>
                      </div>
                    )}
                    {renewalDate && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--slate)' }}>{isFreeTrial ? 'Trial ends' : (currentAutoRenew ? 'Renews' : 'Expires')}</span>
                        <span style={{ fontWeight: 500 }}>{renewalDate}</span>
                      </div>
                    )}
                  </div>
                </div>

                {hasSubscription && !isFreeTrial && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderTop: '1px solid var(--border-subtle)' }}>
                    <span style={{ color: 'var(--charcoal)', fontWeight: 500 }}>Auto-renew</span>
                    <Form.Check
                      type="switch"
                      id="billing-modal-auto-renew"
                      checked={currentAutoRenew}
                      onChange={handleToggleAutoRenew}
                      className="profile-menu-autorenew-switch"
                    />
                  </div>
                )}

                {!showLimitedInviteeBilling && (
                <div style={{ border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '14px' }}>
                  <p style={{ fontSize: '12px', color: 'var(--slate)', textAlign: 'center', margin: 0 }}>
                    You will be redirected to PayPal to update your payment details.
                  </p>
                </div>
                )}
                {hasSubscription && (
                  <button
                    onClick={() => { setShowBillingModal(false); setShowCancelConfirmModal(true); }}
                    disabled={cancellingSubscription}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#000', cursor: 'pointer', fontSize: '14px', textDecoration: 'underline', display: 'block', margin: '0 auto' }}
                  >
                    Cancel Subscription
                  </button>
                )}
              </div>
              </>
              )}
            </div>
          </CustomModal>
        );
      })()}

      {/* Upgrade Plan Modal */}
      <CustomModal
        show={showSubscriptionModal}
        onHide={() => setShowSubscriptionModal(false)}
        title="Upgrade Plan"
        subtitle="Unlock premium features with our Pro plans"
        iconVariant="gold"
        size="lg"
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        }
      >
        {renderUpgradeModalContent()}
      </CustomModal>
    </>
  );
};

export default Navbar;
